import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import Empty from '@/assets/icons/empty.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';

type DialogState = {
  openingTitle: string;
  hiringGoalUrl?: string;
};

type OpeningDetailsParams = {
  title: string;
  brandSlug?: string | null;
  locationId?: string;
  funnelId?: string;
};

type NoHiringGoalDialogContextType = {
  openNoHiringGoalDialog: (opening: OpeningDetailsParams) => void;
};

const NoHiringGoalDialogContext = createContext<
  NoHiringGoalDialogContextType | undefined
>(undefined);

export function NoHiringGoalDialogProvider({ children }: PropsWithChildren) {
  const { getHireATHiringGoalUrl } = useHireUrl();
  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const openNoHiringGoalDialog = useCallback(
    ({ title, brandSlug, locationId, funnelId }: OpeningDetailsParams) => {
      const hiringGoalUrl =
        locationId &&
        funnelId &&
        getHireATHiringGoalUrl(brandSlug ?? 'no_brand', locationId, funnelId);

      setDialogState({
        openingTitle: title,
        hiringGoalUrl,
      });
    },
    [getHireATHiringGoalUrl],
  );

  const handleClose = () => {
    setDialogState(null);
  };

  return (
    <NoHiringGoalDialogContext.Provider
      value={{
        openNoHiringGoalDialog,
      }}
    >
      {children}
      {dialogState && (
        <Dialog
          open
          onOpenChange={open => {
            if (!open) handleClose();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <FormattedMessage
                  defaultMessage="Hiring goal required!"
                  description="Blocking modal title when user opens channels without a goal"
                />
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center gap-2 px-6 py-4">
              <Typography variant="subtitle1">
                <FormattedMessage
                  defaultMessage="This opening doesn't have an active or a past hiring goal"
                  description="Explains why sourcing views are blocked until a goal exists"
                />
              </Typography>
              <img src={Empty} alt="Empty" />
              <Typography variant="body2">
                <FormattedMessage
                  defaultMessage="Add a hiring goal to get started"
                  description="Short subheading above the longer CTA paragraph"
                />
              </Typography>

              <Typography variant="subtitle1" className="text-center">
                <FormattedMessage
                  defaultMessage="Please add a hiring goal to {openingTitle} to track sourcing performance, insights & recommendations to help you achieve your goal!"
                  description="Call-to-action when an opening is missing a hiring goal for reporting"
                  values={{
                    openingTitle: <strong>{dialogState.openingTitle}</strong>,
                  }}
                />
              </Typography>
            </div>

            <DialogFooter>
              {dialogState.hiringGoalUrl ? (
                <Button asChild>
                  <Link to={dialogState.hiringGoalUrl} target="_blank">
                    <ExternalLinkIcon />
                    <FormattedMessage
                      defaultMessage="Add hiring goal"
                      description="Enabled: opens Hire in new tab to add a goal"
                    />
                  </Link>
                </Button>
              ) : (
                <Button disabled>
                  <ExternalLinkIcon />
                  <FormattedMessage
                    defaultMessage="Add hiring goal"
                    description="Same label with button disabled if Hire URL is missing"
                  />
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </NoHiringGoalDialogContext.Provider>
  );
}

export function useNoHiringGoalDialog() {
  const context = useContext(NoHiringGoalDialogContext);

  if (!context) {
    throw new Error(
      'useNoHiringGoalDialog must be used within a NoHiringGoalDialogProvider',
    );
  }

  return context;
}
